import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import {
  Upload,
  Trash2,
  GripVertical,
  Image as ImageIcon,
  Loader2,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SERVICES = [
  { slug: "electrical", title: "Electrical" },
  { slug: "painting", title: "Painting" },
  { slug: "flooring", title: "Flooring" },
  { slug: "hvac", title: "HVAC" },
  { slug: "plumbing", title: "Plumbing" },
  { slug: "roofing", title: "Roofing" },
  { slug: "landscaping", title: "Landscaping" },
  { slug: "kitchen-bath", title: "Kitchen & Bath" },
  { slug: "construction", title: "Construction" },
  { slug: "renovation", title: "Renovation" },
];

function UploadButton({ onUpload, uploading, label, accept, testId }) {
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("File must be JPG, PNG, or WebP");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }
    await onUpload(file);
    e.target.value = "";
  };

  return (
    <label className="relative cursor-pointer" data-testid={testId}>
      <input type="file" accept={accept || "image/jpeg,image/png,image/webp"} onChange={handleFile} className="hidden" disabled={uploading} />
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
        uploading ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-white border-slate-200 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 text-[#0B1F3B]"
      }`}>
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {label}
      </span>
    </label>
  );
}

function ServiceImagePanel({ slug, title }) {
  const [data, setData] = useState(null);
  const [heroUploading, setHeroUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/service-images/${slug}`);
      setData(res.data);
    } catch {
      setData({ slug, hero_url: null, gallery: [] });
    }
  }, [slug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const uploadHero = async (file) => {
    setHeroUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/admin/service-images/${slug}/hero`, form);
      setData((prev) => ({ ...prev, hero_url: res.data.hero_url }));
      toast.success(`Hero image updated for ${title}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setHeroUploading(false);
    }
  };

  const uploadGallery = async (file) => {
    setGalleryUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/admin/service-images/${slug}/gallery`, form);
      setData((prev) => ({
        ...prev,
        gallery: [...(prev?.gallery || []), res.data],
      }));
      toast.success("Image added to gallery");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setGalleryUploading(false);
    }
  };

  const deleteGalleryImage = async (imageId) => {
    if (!window.confirm("Delete this image? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/service-images/${slug}/gallery/${imageId}`);
      setData((prev) => ({
        ...prev,
        gallery: (prev?.gallery || []).filter((img) => img.id !== imageId),
      }));
      toast.success("Image deleted");
    } catch {
      toast.error("Failed to delete image");
    }
  };

  const moveImage = async (idx, direction) => {
    const gallery = [...(data?.gallery || [])];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= gallery.length) return;
    const temp = gallery[idx];
    gallery[idx] = gallery[newIdx];
    gallery[newIdx] = temp;
    setData((prev) => ({ ...prev, gallery }));
    try {
      await axios.put(`${API}/admin/service-images/${slug}/gallery/reorder`, {
        order: gallery.map((img) => img.id),
      });
    } catch {
      toast.error("Failed to reorder");
      fetchData();
    }
  };

  const gallery = data?.gallery || [];
  const heroUrl = data?.hero_url;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`service-panel-${slug}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0B1F3B]/5 flex items-center justify-center overflow-hidden">
            {heroUrl ? (
              <img src={heroUrl} alt={title} className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="w-5 h-5 text-slate-400" />
            )}
          </div>
          <div className="text-left">
            <p className="font-semibold text-[#0B1F3B]">{title}</p>
            <p className="text-xs text-slate-500">
              {heroUrl ? "Hero uploaded" : "No hero"} · {gallery.length}/8 gallery
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-6">
          {/* Hero Image */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-[#0B1F3B] text-sm">Hero Image</h4>
              <UploadButton onUpload={uploadHero} uploading={heroUploading} label={heroUrl ? "Replace Hero" : "Upload Hero"} testId={`upload-hero-${slug}`} />
            </div>
            {heroUrl && (
              <div className="rounded-lg overflow-hidden border border-slate-200" style={{ maxHeight: 200 }}>
                <img src={heroUrl} alt={`${title} hero`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
          </div>

          {/* Gallery */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-[#0B1F3B] text-sm">Gallery ({gallery.length}/8)</h4>
              {gallery.length < 8 && (
                <UploadButton onUpload={uploadGallery} uploading={galleryUploading} label="Add Image" testId={`upload-gallery-${slug}`} />
              )}
            </div>
            {gallery.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {gallery.map((img, idx) => (
                  <div key={img.id} className="group relative rounded-lg overflow-hidden border border-slate-200 aspect-[4/3]">
                    <img src={img.url} alt={img.label || `${title} gallery ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      {idx > 0 && (
                        <button onClick={() => moveImage(idx, -1)} className="p-1.5 bg-white rounded-lg shadow" title="Move left">
                          <GripVertical className="w-3.5 h-3.5 text-slate-600" />
                        </button>
                      )}
                      <button onClick={() => deleteGalleryImage(img.id)} className="p-1.5 bg-red-500 rounded-lg shadow" title="Delete">
                        <Trash2 className="w-3.5 h-3.5 text-white" />
                      </button>
                      {idx < gallery.length - 1 && (
                        <button onClick={() => moveImage(idx, 1)} className="p-1.5 bg-white rounded-lg shadow" title="Move right">
                          <GripVertical className="w-3.5 h-3.5 text-slate-600" />
                        </button>
                      )}
                    </div>
                    <span className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">{idx + 1}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <ImageIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No gallery images yet</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ServiceImagesManager() {
  return (
    <div className="space-y-3" data-testid="service-images-manager">
      <p className="text-sm text-slate-500 mb-4">
        Upload hero and gallery images for each service page. Images are auto-optimized and served via CDN. No redeploy needed.
      </p>
      {SERVICES.map((svc) => (
        <ServiceImagePanel key={svc.slug} slug={svc.slug} title={svc.title} />
      ))}
    </div>
  );
}
